import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Search, Mail, Clock, User, Newspaper, Sparkles, Truck, Calendar, Cog, ShoppingCart, Puzzle, GraduationCap } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useBlogPosts } from "@/data/hooks";
import Seo from "@/components/seo/Seo";

const categories = [
  "All",
  "Blog",
  "Product Spotlight",
  "Supplier Insights",
  "Events",
  "Advance ERP",
  "Aether eCommerce",
  "Integrated Partner",
  "Antera Academy",
];

const categoryColors = {
  "Product Spotlight": "bg-sky-50 text-sky-700 border-sky-200",
  "Supplier Insights": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Blog": "bg-amber-50 text-amber-700 border-amber-200",
  "Events": "bg-violet-50 text-violet-700 border-violet-200",
  "Advance ERP": "bg-indigo-50 text-indigo-700 border-indigo-200",
  "Aether eCommerce": "bg-cyan-50 text-cyan-700 border-cyan-200",
  "Integrated Partner": "bg-rose-50 text-rose-700 border-rose-200",
  "Antera Academy": "bg-orange-50 text-orange-700 border-orange-200",
};

// Branded gradient + icon per category for the "no image" thumbnail fallback.
// Keeps every card visually consistent even when a post has no hero image.
const categoryTile = {
  "Product Spotlight": { gradient: "from-sky-500 to-sky-700",       Icon: Sparkles },
  "Supplier Insights": { gradient: "from-emerald-500 to-emerald-700", Icon: Truck },
  "Blog":               { gradient: "from-amber-500 to-amber-700",   Icon: Newspaper },
  "Events":             { gradient: "from-violet-500 to-violet-700", Icon: Calendar },
  "Advance ERP":        { gradient: "from-indigo-500 to-indigo-700", Icon: Cog },
  "Aether eCommerce":   { gradient: "from-cyan-500 to-cyan-700",     Icon: ShoppingCart },
  "Integrated Partner": { gradient: "from-rose-500 to-rose-700",     Icon: Puzzle },
  "Antera Academy":     { gradient: "from-orange-500 to-orange-700", Icon: GraduationCap },
};
const defaultTile = { gradient: "from-slate-600 to-slate-800", Icon: Newspaper };

export default function BlogPage() {
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const { posts: blogPosts, loading } = useBlogPosts();

  const filtered = blogPosts.filter((post) => {
    const matchesCategory =
      activeCategory === "All" ||
      (post.tags && post.tags.includes(activeCategory)) ||
      post.category === activeCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const featured = blogPosts[0];
  const rest = filtered.slice(activeCategory === "All" && !searchQuery ? 1 : 0);

  return (
    <div data-testid="blog-page" className="page-transition">
      <Seo
        title="Antera Insights | Blog on Promo & Print Software"
        description="Industry insights, product spotlights, and best practices for promotional products distributors, suppliers, decorators, print shops, and sign companies running Antera."
        keywords="promotional products software blog, promo industry automation, promo industry workflow automation, antera insights, promostandards software, promotional products erp"
        path="/blog"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Antera Insights",
          description: "Industry insights, product spotlights, and best practices for promotional products distributors, suppliers, decorators, print shops, and sign companies running Antera.",
          url: "https://anterasoftware.com/blog",
          isPartOf: {
            "@type": "WebSite",
            name: "Antera Software",
            url: "https://anterasoftware.com",
          },
          publisher: {
            "@type": "Organization",
            name: "Antera Software",
            logo: { "@type": "ImageObject", url: "/api/media/adff924b-451a-45d0-a130-026ae966ce9a" },
          },
          hasPart: blogPosts.slice(0, 10).map((p) => ({
            "@type": "BlogPosting",
            headline: p.title,
            url: `https://anterasoftware.com/blog/${p.slug}`,
            datePublished: p.date,
            image: p.image || undefined,
            author: { "@type": "Person", name: p.author || "Antera Software" },
          })),
        }}
      />
      {/* Banner */}
      <section className="pt-28 lg:pt-36 bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-[15%] w-72 h-72 bg-sky-500/8 rounded-full blur-[100px]" />
          <div className="absolute bottom-0 right-[10%] w-96 h-96 bg-sky-400/6 rounded-full blur-[120px]" />
          {/* Wave decoration */}
          <svg className="absolute -bottom-[1px] left-0 w-full block" viewBox="0 0 1440 60" fill="none" preserveAspectRatio="none" aria-hidden="true">
            <path d="M0 61V30C240 0 480 0 720 30C960 60 1200 60 1440 30V61H0Z" fill="white" />
          </svg>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative pb-16 lg:pb-20">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
              <div>
                <span className="text-xs font-semibold text-sky-400 tracking-widest uppercase">Industry &amp; Product News</span>
                <h1 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white">
                  Antera Insights
                </h1>
                <p className="mt-3 text-base text-slate-400 max-w-lg">
                  Be the first to read industry perspectives, product notes, and Antera news.
                </p>
              </div>
              <div className="relative w-full lg:w-80">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search articles..."
                  className="pl-10 h-10 text-sm border-slate-700 bg-slate-800/60 text-white placeholder:text-slate-500 rounded-lg focus:border-sky-500"
                  data-testid="blog-search"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Category Filters */}
      <section className="pt-6 pb-8 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                data-testid={`blog-filter-${cat.toLowerCase().replace(/\s/g, "-")}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all border ${
                  activeCategory === cat
                    ? "bg-sky-600 text-white border-sky-600"
                    : "bg-white text-slate-600 border-slate-200 hover:border-sky-300 hover:text-sky-600"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Post - Compact horizontal */}
      {activeCategory === "All" && !searchQuery && featured && (
        <section className="pb-8 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link to={`/blog/${featured.slug}`}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                data-testid="blog-featured-post"
                className="group flex flex-col sm:flex-row gap-5 bg-sky-50/50 border border-sky-100 rounded-xl p-5 hover:shadow-md transition-all duration-300"
              >
                {featured.image && (
                  <div className="w-full sm:w-44 h-32 sm:h-28 rounded-lg overflow-hidden bg-slate-100 shrink-0">
                    <img src={featured.image} alt={featured.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                )}
                <div className="flex-1 flex flex-col justify-center min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[10px] font-bold text-sky-600 uppercase tracking-wider">Latest</span>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${categoryColors[featured.category] || "bg-slate-50 text-slate-600 border-slate-200"}`}>
                      {featured.category}
                    </span>
                    <span className="text-xs text-slate-400">{featured.date}</span>
                  </div>
                  <h2 className="text-lg font-bold text-slate-900 group-hover:text-sky-600 transition-colors leading-snug">
                    {featured.title}
                  </h2>
                  <p className="mt-1.5 text-sm text-slate-500 leading-relaxed line-clamp-2">{featured.excerpt}</p>
                  <div className="mt-3 flex items-center gap-4">
                    <span className="text-sm font-semibold text-sky-600 inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                      Read Article <ArrowRight className="h-3.5 w-3.5" />
                    </span>
                    {featured.author && <span className="text-xs text-slate-400 flex items-center gap-1"><User className="h-3 w-3" /> {featured.author}</span>}
                    {featured.readTime && <span className="text-xs text-slate-400 flex items-center gap-1"><Clock className="h-3 w-3" /> {featured.readTime}</span>}
                  </div>
                </div>
              </motion.div>
            </Link>
          </div>
        </section>
      )}

      {/* Posts List */}
      <section className="pb-12 lg:pb-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-16">
              <p className="text-slate-400 text-base">Loading articles…</p>
            </div>
          ) : rest.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-slate-400 text-base">No articles found matching your search.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {rest.map((post, i) => (
                <motion.article
                  key={post.slug}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: Math.min(i * 0.02, 0.2), duration: 0.3 }}
                  data-testid={`blog-card-${i}`}
                >
                  <Link
                    to={`/blog/${post.slug}`}
                    className="group flex gap-4 items-center p-4 rounded-xl border border-slate-100 hover:border-slate-200 hover:bg-slate-50/50 hover:shadow-sm transition-all duration-200"
                  >
                    {/* Thumbnail */}
                    {post.image ? (
                      <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-lg overflow-hidden bg-slate-100 shrink-0">
                        <img src={post.image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      </div>
                    ) : (() => {
                      const tile = categoryTile[post.category] || defaultTile;
                      const { Icon } = tile;
                      return (
                        <div
                          className={`w-20 h-20 sm:w-24 sm:h-24 rounded-lg overflow-hidden shrink-0 bg-gradient-to-br ${tile.gradient} flex items-center justify-center relative group-hover:brightness-110 transition-all duration-300`}
                          data-testid={`blog-fallback-tile-${post.category?.toLowerCase().replace(/\s/g, "-") || "default"}`}
                        >
                          <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_20%_20%,white_0%,transparent_50%)]" />
                          <Icon className="h-8 w-8 text-white/90 relative z-10" strokeWidth={1.5} />
                        </div>
                      );
                    })()}

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${categoryColors[post.category] || "bg-slate-50 text-slate-600 border-slate-200"}`}>
                          {post.category}
                        </span>
                        <span className="text-xs text-slate-400">{post.date}</span>
                      </div>
                      <h3 className="text-sm sm:text-base font-bold text-slate-900 group-hover:text-sky-600 transition-colors leading-snug line-clamp-1">
                        {post.title}
                      </h3>
                      <p className="text-sm text-slate-500 leading-relaxed line-clamp-1 mt-0.5 hidden sm:block">{post.excerpt}</p>
                    </div>

                    {/* Right meta */}
                    <div className="hidden md:flex flex-col items-end gap-1 shrink-0 text-xs text-slate-400">
                      {post.author && <span>{post.author}</span>}
                      {post.readTime && <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {post.readTime}</span>}
                    </div>

                    <ArrowRight className="h-4 w-4 text-slate-300 group-hover:text-sky-500 shrink-0 transition-colors hidden sm:block" />
                  </Link>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-14 lg:py-16 bg-slate-50 border-t border-slate-200">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-12 h-12 rounded-full bg-sky-100 flex items-center justify-center mx-auto mb-4">
            <Mail className="h-6 w-6 text-sky-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900">Subscribe to Newsletter</h2>
          <p className="mt-2 text-sm text-slate-500">Be the first to read industry perspectives, product notes, and Antera news.</p>
          <a
            href="https://lp.constantcontactpages.com/sl/hVe3pRw/TheAnteraExchange"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block mt-5"
          >
            <Button className="bg-sky-600 hover:bg-sky-700 text-white rounded-full px-8 h-11 text-sm font-semibold">
              Subscribe <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
}
